import { useMemo } from 'react'
import { FileSearch, MoreHorizontal, Pencil, Trash2, Eye, Paperclip, CalendarPlus } from 'lucide-react'
import {
  DataTable,
  type DataTableColumn,
  type DataTablePagination,
  type BulkAction,
  type RowSelectionState,
  type InlineEditState,
  StatusBadge,
  EmptyState,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Button,
} from '@atrvd/saas-ui'
import { formatCategoryAmount, formatDate, formatDateTime } from '@dcp/shared/utils/formatters'
import type { ProjectionWithRelations } from '@/lib/supabase'
import { getProjectionAmountInfo } from '@/lib/projection-utils'
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs'

/** Maximum allowed amount for inline edit validation */
const MAX_PROJECTION_AMOUNT = 1_000_000_000

/** Human-readable display string for the maximum amount */
const MAX_AMOUNT_DISPLAY = '$1B'

/** Maximum decimal places allowed for financial amounts */
const MAX_DECIMAL_PLACES = 2

/**
 * Standard decimal numbers with an optional leading minus sign:
 * digits, optional dot, optional decimals (no hex, scientific notation, etc.).
 * DCP-250 AC#4: negatives are valid (outflow), so the sign is permitted here.
 */
const DECIMAL_NUMBER_REGEX = /^-?\d+(\.\d+)?$/

/** Validation error messages for inline amount editing */
const VALIDATION_MESSAGES = {
  NOT_A_NUMBER: 'Must be a number',
  TOO_LARGE: `Amount seems too large (max ${MAX_AMOUNT_DISPLAY})`,
  TOO_MANY_DECIMALS: `Maximum ${MAX_DECIMAL_PLACES} decimal places allowed`,
} as const

interface ProjectionsTableProps {
  projections: ProjectionWithRelations[]
  onEdit: (projection: ProjectionWithRelations) => void
  onDelete: (projectionId: string) => void
  onView?: (projection: ProjectionWithRelations) => void
  onMoveToNextDay?: (projection: ProjectionWithRelations) => void
  onClearFilters?: () => void
  page?: number
  pageSize?: number
  totalCount?: number
  onPageChange?: (page: number) => void
  /** Set of projection IDs to highlight with a fade-out animation (e.g. newly created rows) */
  highlightedIds?: Set<string>
  /** Current search term to highlight matching text in Facility, Category, Description columns */
  searchTerm?: string
  /** Row selection state for multiselect (controlled by parent) */
  selection?: RowSelectionState<ProjectionWithRelations>
  /** Bulk action buttons shown when rows are selected */
  bulkActions?: BulkAction<ProjectionWithRelations>[]
  /** Inline editing configuration for description and amount cells */
  editing?: InlineEditState<ProjectionWithRelations>
  /** Whether the table data is currently loading */
  loading?: boolean
}

/**
 * Highlights occurrences of a search term within text (case-insensitive).
 * Wraps matched substrings in a <mark> tag with a yellow background.
 */
function HighlightMatch({ text, term }: { text: string; term?: string }) {
  if (!term?.trim() || !text) return <>{text}</>

  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'))

  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === term.toLowerCase() ? (
          <mark key={i} className="bg-yellow-200 text-inherit rounded-sm px-0.5">{part}</mark>
        ) : (
          part
        )
      )}
    </>
  )
}

/**
 * Get actions dropdown menu for a projection row
 * Provides View, Edit, Move to next day, and Delete options
 */
function getActionsMenu(
  projection: ProjectionWithRelations,
  onView: ((projection: ProjectionWithRelations) => void) | undefined,
  onEdit: (projection: ProjectionWithRelations) => void,
  onDelete: (projectionId: string) => void,
  onMoveToNextDay?: (projection: ProjectionWithRelations) => void
) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onView && (
          <DropdownMenuItem onClick={() => onView(projection)}>
            <Eye className="mr-2 h-4 w-4" />
            View
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => onEdit(projection)}>
          <Pencil className="mr-2 h-4 w-4" />
          Edit
        </DropdownMenuItem>
        {onMoveToNextDay && (
          <DropdownMenuItem onClick={() => onMoveToNextDay(projection)}>
            <CalendarPlus className="mr-2 h-4 w-4" />
            Move to next day
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => onDelete(projection.id)} className="text-red-600">
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Projections table component matching the UI mockup design
 * Displays daily cashflow projections with all required columns
 */
export function ProjectionsTable({
  projections,
  onEdit,
  onDelete,
  onView,
  onMoveToNextDay,
  onClearFilters,
  page = 1,
  pageSize = 10,
  totalCount = 0,
  onPageChange,
  highlightedIds,
  searchTerm,
  selection,
  bulkActions,
  editing,
  loading,
}: ProjectionsTableProps) {
  /**
   * Column definitions matching the user's required field order.
   * Memoized to prevent re-creating render functions on every parent re-render.
   */
  const columns = useMemo<DataTableColumn<ProjectionWithRelations>[]>(() => [
    {
      key: 'facility',
      header: 'Facility',
      render: (projection) => (
        <span className="text-sm">
          <HighlightMatch text={projection.facility?.name || '-'} term={searchTerm} />
        </span>
      ),
      width: '120px',
    },
    {
      key: 'category',
      header: 'Transaction category',
      render: (projection) => (
        <span className="text-sm" title={projection.category.name}>
          <HighlightMatch text={projection.category.name} term={searchTerm} />
        </span>
      ),
      width: '160px',
    },
    {
      key: 'date',
      header: 'Projected Payment Date',
      render: (projection) => formatDate(projection.date),
      width: '160px',
      // Anchor column — always visible, cannot be hidden via the Columns menu.
      // Same choice as ManualTransactionsTable: the date is what identifies a
      // projection row to the reader, so hiding it would leave a table of
      // amounts nobody can place in time.
      disableHide: true,
    },
    {
      key: 'description',
      header: 'Description',
      render: (projection) => (
        <span className="max-w-[150px] truncate block" title={projection.description || ''}>
          <HighlightMatch text={projection.description || '-'} term={searchTerm} />
        </span>
      ),
      width: '150px',
      editable: !!editing,
      editConfig: { type: 'input', editTrigger: 'doubleClick', showActions: false },
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (projection) => {
        // DCP-250 AC#3/#4: render the SIGNED amount — negatives show in red with
        // parentheses, positives in green. Zero renders as a muted placeholder.
        const { amount } = getProjectionAmountInfo(projection)
        if (amount === 0) {
          return <span className="font-medium text-muted-foreground">-</span>
        }
        const { text, className } = formatCategoryAmount(amount)
        return <span className={`font-medium ${className}`}>{text}</span>
      },
      cellClassName: 'text-right',
      width: '120px',
      editable: !!editing,
      // Return null for zero amounts so input appears empty rather than showing "0".
      // Returns the SIGNED amount (negatives included) for editing.
      editAccessor: (projection): number | null => {
        const { amount } = getProjectionAmountInfo(projection)
        return amount !== 0 ? amount : null
      },
      editConfig: {
        type: 'input',
        editTrigger: 'doubleClick',
        showActions: false,
        validate: (value: unknown): string | null => {
          if (value === '' || value === null || value === undefined) return null
          const trimmed = String(value).trim()
          if (trimmed === '') return null
          // Only accept standard decimal numbers, optionally signed (no hex, scientific notation, etc.)
          if (!DECIMAL_NUMBER_REGEX.test(trimmed)) return VALIDATION_MESSAGES.NOT_A_NUMBER
          const num = Number(trimmed)
          // DCP-250 AC#4: negatives are valid; cap on magnitude (absolute value).
          if (Math.abs(num) > MAX_PROJECTION_AMOUNT) return VALIDATION_MESSAGES.TOO_LARGE
          // Regex already guarantees standard decimal format, check decimal places directly
          const dot = trimmed.indexOf('.')
          if (dot !== -1 && trimmed.length - dot - 1 > MAX_DECIMAL_PLACES) {
            return VALIDATION_MESSAGES.TOO_MANY_DECIMALS
          }
          return null
        },
      },
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (projection) => (
        <StatusBadge
          status={projection.priority ? 'error' : 'info'}
          label={projection.priority ? 'Priority' : 'Normal'}
          showIcon={false}
        />
      ),
      cellClassName: 'whitespace-nowrap',
      width: '150px',
    },
    {
      key: 'include_in_report',
      header: 'Include / exclude from Projection Report',
      render: (projection) => (
        <StatusBadge
          status={projection.include_in_report ? 'success' : 'warning'}
          label={projection.include_in_report ? 'Include' : 'Exclude'}
          showIcon={false}
        />
      ),
      width: '180px',
    },
    {
      key: 'created',
      header: 'Created timestamp',
      render: (projection) => formatDateTime(projection.created_at),
      width: '150px',
    },
    {
      key: 'attachment',
      header: 'Attachment',
      render: (projection) => (
        <div className="flex items-center justify-center">
          {projection.attachment_url ? (
            <a
              href={projection.attachment_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80"
              title={projection.attachment_name || 'View attachment'}
            >
              <Paperclip className="h-4 w-4" />
            </a>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </div>
      ),
      cellClassName: 'text-center',
      width: '100px',
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (projection) => getActionsMenu(projection, onView, onEdit, onDelete, onMoveToNextDay),
      cellClassName: 'text-right',
      width: '80px',
      // View/Edit/Delete/Move must stay reachable regardless of saved
      // visibility — never hideable (mirrors every other table here).
      disableHide: true,
    },
  ], [editing, searchTerm, onView, onEdit, onDelete, onMoveToNextDay])

  // DCP-342: per-user column visibility + order, persisted in localStorage —
  // the "Columns" menu and drag-reorder this table was missing while every other
  // table in Banking already had them. Keyed 'projections' so the preferences do
  // not collide with the other tables' saved state.
  const columnPrefs = useTableColumnPrefs('projections', columns)

  // Calculate total pages for pagination
  const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0

  // Pagination configuration for DataTable
  const paginationConfig: DataTablePagination | undefined =
    totalCount > 0 && onPageChange
      ? {
          currentPage: page,
          totalPages,
          pageSize,
          totalItems: totalCount,
          onPageChange,
        }
      : undefined

  return (
    <DataTable
      title="Daily Projections"
      className="min-h-[680px]"
      columns={columns}
      data={projections}
      loading={loading}
      pagination={paginationConfig}
      rowKey={(projection) => projection.id}
      rowClassName={(projection) =>
        highlightedIds?.has(projection.id) ? 'animate-highlight-fade' : ''
      }
      selection={selection}
      bulkActions={bulkActions}
      editing={editing}
      emptyState={
        <EmptyState
          icon={FileSearch}
          title="No projections found"
          description="Adjust filters or create projections to view data"
          action={onClearFilters ? { label: 'Clear Filters', onClick: onClearFilters } : undefined}
        />
      }
      itemName="projections"
      enableColumnReorder
      {...columnPrefs}
    />
  )
}
